// AI content generation types and schemas
import { z } from "zod";
import { DisciplineSlugSchema, SkillTagsSchema, } from "./skills.js";
// ==================== Shared Schemas ====================
/** ISO datetime string schema */
const ISODateTimeSchema = z.string().refine((val) => {
    const date = new Date(val);
    return !isNaN(date.getTime());
}, { message: "Invalid ISO datetime string" });
// ==================== Content Review Status ====================
/** Review status for AI-generated content */
export const ContentReviewStatusValues = [
    "pending",
    "approved",
    "rejected",
    "needs_revision",
];
export const ContentReviewStatusSchema = z.enum(ContentReviewStatusValues);
export const AIMetadataSchema = z.object({
    generatedAt: ISODateTimeSchema,
    agentVersion: z.string(),
    modelUsed: z.string(),
    generationPrompt: z.string().optional(),
    generationRunId: z.string().uuid().optional(),
    tokenUsage: z
        .object({
        inputTokens: z.number().int().nonnegative(),
        outputTokens: z.number().int().nonnegative(),
    })
        .optional(),
});
// ==================== Challenge Type Schema ====================
/** All valid challenge types */
export const ChallengeTypeValues = [
    "counting",
    "number_recognition",
    "addition",
    "subtraction",
    "comparison",
    "beginning_sound",
    "letter_recognition",
    "word_building",
    "pattern_sequence",
    "color_pattern",
    "key_finding",
    "sequence_typing",
    "word_typing",
    "maze",
    "single_command",
];
export const ChallengeTypeSchema = z.enum(ChallengeTypeValues);
/**
 * Zod schema for generated challenge content
 * Uses loose validation for content - specific content validated separately
 */
export const GeneratedChallengeContentSchema = z.object({
    type: ChallengeTypeSchema,
    content: z.record(z.string(), z.unknown()),
    skillTags: SkillTagsSchema,
    difficulty: z.number().int().min(1).max(5),
    promptAudio: z.string().optional(),
    promptText: z.string().optional(),
});
export const ContentGenerationRequestSchema = z.object({
    discipline: DisciplineSlugSchema,
    level: z.number().int().min(1).max(5),
    challengeType: ChallengeTypeSchema,
    count: z.number().int().min(1).max(10),
    targetSkills: SkillTagsSchema.optional(),
    customInstructions: z.string().max(1000).optional(),
});
export const ContentGenerationResponseSchema = z.object({
    success: z.boolean(),
    challenges: z.array(GeneratedChallengeContentSchema),
    allSkillTags: SkillTagsSchema,
    metadata: AIMetadataSchema,
    error: z.string().optional(),
});
export const ChallengeWithAIFieldsSchema = z.object({
    id: z.string().uuid(),
    levelId: z.string().uuid(),
    challengeNumber: z.number().int().positive(),
    type: ChallengeTypeSchema,
    content: z.record(z.string(), z.unknown()),
    difficulty: z.number().int().min(1).max(5),
    isActive: z.boolean(),
    skillTags: SkillTagsSchema,
    aiMetadata: AIMetadataSchema.optional(),
    reviewStatus: ContentReviewStatusSchema,
    reviewedBy: z.string().uuid().optional(),
    reviewedAt: ISODateTimeSchema.optional(),
    reviewNotes: z.string().optional(),
});
export const AIGenerationHistorySchema = z.object({
    id: z.string().uuid(),
    adminId: z.string().uuid(),
    discipline: DisciplineSlugSchema,
    level: z.number().int().min(1).max(5),
    challengeType: ChallengeTypeSchema,
    count: z.number().int().min(1).max(10),
    targetSkills: SkillTagsSchema.optional(),
    modelUsed: z.string(),
    createdAt: ISODateTimeSchema,
    status: z.enum(["pending", "completed", "failed", "partial"]),
    challengesGenerated: z.number().int().nonnegative(),
    challengeIds: z.array(z.string().uuid()).optional(),
    errorMessage: z.string().optional(),
});
export const BulkSaveChallengesRequestSchema = z.object({
    levelId: z.string().uuid(),
    challenges: z.array(z.object({
        type: ChallengeTypeSchema,
        content: z.record(z.string(), z.unknown()),
        skillTags: SkillTagsSchema,
        difficulty: z.number().int().min(1).max(5),
        aiMetadata: AIMetadataSchema,
    })),
    generationRunId: z.string().uuid().optional(),
});
export const BulkSaveChallengesResponseSchema = z.object({
    success: z.boolean(),
    createdIds: z.array(z.string().uuid()),
    count: z.number().int().nonnegative(),
    errors: z
        .array(z.object({
        index: z.number().int().nonnegative(),
        message: z.string(),
    }))
        .optional(),
});
// ==================== Validation Helpers ====================
/**
 * Validate a content generation request
 * Returns the parsed schema type - use type assertions if you need the interface type
 */
export function validateContentGenerationRequest(data) {
    return ContentGenerationRequestSchema.parse(data);
}
/**
 * Validate generated challenge content
 * Returns the parsed schema type - use type assertions if you need the interface type
 */
export function validateGeneratedChallengeContent(data) {
    return GeneratedChallengeContentSchema.parse(data);
}
/**
 * Safely parse a content generation request (returns null on error)
 */
export function safeParseContentGenerationRequest(data) {
    const result = ContentGenerationRequestSchema.safeParse(data);
    if (result.success) {
        return { success: true, data: result.data };
    }
    return { success: false, error: result.error };
}
//# sourceMappingURL=ai-content.js.map